import { memo, useCallback, useMemo } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { TextField } from 'components/mui/common/TextField';
import { useModals } from 'utils/hooks';
import type { FormItemDefinition, FormItemUiDefinition } from 'types/formItem';
import { FileEditModal } from '../FileUpload/modals/FileEditModal';
import { toFileIds, type FileUploadFormValue } from '../FileUpload/utils/toFileIds';
import type { DocumentEditingOptions } from 'types/documentEditing';

type FileUploadsItemEditableProps = {
  documentEditing?: DocumentEditingOptions;
  id: string;
  definition?: FormItemDefinition;
  disabled?: boolean;
  background?: 'white' | 'transparent';
  formItemUiDefinition?: FormItemUiDefinition | null;
};

const FileUploadsItemEditableBase = ({
  id,
  definition,
  disabled,
  background,
  formItemUiDefinition,
  documentEditing,
}: FileUploadsItemEditableProps) => {
  const { t } = useTranslation();
  const { control, setValue } = useFormContext();
  const [modals, openModal, closeModal] = useModals({ file: false });

  const watchedValue = useWatch({ control, name: id }) as FileUploadFormValue[] | string[] | null | undefined;
  const fileIds = useMemo(() => toFileIds(watchedValue), [watchedValue]);

  const { maxFilesNumber, isRequired = false } = definition?.fileUpload ?? {};

  const handleOpen = useCallback(() => {
    if (disabled) return;

    openModal('file')();
  }, [disabled, openModal]);

  return (
    <>
      <Controller
        control={control}
        name={id}
        rules={{
          required: {
            value: isRequired,
            message: t('common.required'),
          },
        }}
        render={({ field: { value: _value, onChange: _onChange, ...field }, fieldState: { error } }) => (
          <TextField
            {...field}
            value={`${t('common.count')}: ${fileIds.length}`}
            onClick={handleOpen}
            error={Boolean(error)}
            helperText={error?.message}
            disabled={disabled}
            background={background}
            formItemUiDefinition={formItemUiDefinition}
            fullWidth
            sx={{ paddingTop: 0, cursor: disabled ? 'default' : 'pointer' }}
            dataTestId="edit-file-field"
            slotProps={{
              htmlInput: {
                readOnly: true,
              },
            }}
          />
        )}
      />
      <FileEditModal
        documentEditing={documentEditing}
        open={modals.file}
        onClose={closeModal('file')}
        setValue={setValue}
        fieldName={id}
        value={watchedValue}
        maxFilesNumber={maxFilesNumber}
      />
    </>
  );
};

export const FileUploadsItemEditable = memo(FileUploadsItemEditableBase) as typeof FileUploadsItemEditableBase;
